import { useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  isEmployeeNotInDirectoryError,
  useGetEmployeeFromDirectoryQuery,
} from '@/redux/slices/employeeApiSlice';
import { useGetRequestTypeCategoriesQuery } from '@/redux/slices/organizationApiSlice';
import { EmployeeDirectoryListing } from '@/types/employees';
import { skipToken } from '@reduxjs/toolkit/query';

export const RequestTypeCategoryNameEnum = {
  APPLICATIONS: 'Applications',
  CONNECTIVITY: 'Connectivity',
  DEVICE: 'Device',
  SOMETHING_ELSE: 'Something Else',
};

type RequestTypeCategoryNameEnum =
  (typeof RequestTypeCategoryNameEnum)[keyof typeof RequestTypeCategoryNameEnum];

// With divisions off every employee is visible, so one missing from the
// directory is a data problem, not a restriction: report it instead of letting
// the redacted label hide it. With divisions on, a miss is the expected case.
// useFlags returns {} until LaunchDarkly is ready, so only an explicit `false`
// counts as "divisions off"; an unloaded flag is `undefined`.
export const useReportEmployeeMissingWithoutDivisions = (
  isMissingFromDirectory: boolean,
  organizationId: string,
  employeeId: string | undefined
) => {
  const { ithDivisions } = useFlags();

  useEffect(() => {
    if (isMissingFromDirectory && ithDivisions === false) {
      window.DD_RUM?.addError(
        'Employee missing from the directory in an organization without divisions',
        {
          location: 'useReportEmployeeMissingWithoutDivisions',
          organizationId,
          employeeId: employeeId ?? '',
        }
      );
    }
  }, [isMissingFromDirectory, ithDivisions, organizationId, employeeId]);
};

// Redaction only exists with divisions on. useFlags returns {} until LaunchDarkly
// is ready, so an unloaded flag counts as off, matching the other divisions gates.
export const useIsRedactedByDivisions = (isMissingFromDirectory: boolean) => {
  const { ithDivisions } = useFlags();

  return isMissingFromDirectory && Boolean(ithDivisions);
};

type RequestTypeCategoryChampion = {
  champion: EmployeeDirectoryListing | undefined;
  // True when a champion is assigned but the (division-scoped) directory does
  // not contain them, i.e. the user is not allowed to see who it is.
  isRedacted: boolean;
};

export const useGetRequestTypeCategoryChampion = (
  organizationId: string,
  requestTypeCategoryName: RequestTypeCategoryNameEnum,
  skip?: boolean
): RequestTypeCategoryChampion => {
  const { data: requestTypeCategoriesResponse } =
    useGetRequestTypeCategoriesQuery(
      skip
        ? skipToken
        : {
            organizationId: organizationId,
          }
    );

  const requestTypeCategory = requestTypeCategoriesResponse?.find(
    rtc => rtc.name === requestTypeCategoryName
  );

  const requestTypeCategoryChampionEmployeeId =
    requestTypeCategory?.champions?.[0]?.employeeId;

  const {
    data: requestTypeCategoryChampionEmployee,
    isError,
    error,
  } = useGetEmployeeFromDirectoryQuery(
    {
      // NOTE: This cannot actually ever be an empty string, but the the type requires it, so we're using the skip option to handle when this is empty.
      // At the moment, we only allow one champion per request type category.
      employeeId: requestTypeCategoryChampionEmployeeId ?? '',
      organizationId: organizationId,
    },
    { skip: !requestTypeCategory || !requestTypeCategoryChampionEmployeeId }
  );

  const isMissingFromDirectory = isEmployeeNotInDirectoryError(isError, error);
  const isRedacted = useIsRedactedByDivisions(isMissingFromDirectory);

  useReportEmployeeMissingWithoutDivisions(
    isMissingFromDirectory,
    organizationId,
    requestTypeCategoryChampionEmployeeId
  );

  return { champion: requestTypeCategoryChampionEmployee, isRedacted };
};

export const useGetRequestTypeCategoryChampionEmployee = (
  organizationId: string,
  requestTypeCategoryName: RequestTypeCategoryNameEnum,
  skip?: boolean
): EmployeeDirectoryListing | undefined =>
  useGetRequestTypeCategoryChampion(
    organizationId,
    requestTypeCategoryName,
    skip
  ).champion;
